import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
// pagination removed - render all templates with scroll
import { sessionService } from '@/services/sessionService';
import type { SessionTemplate } from '@arrowise/shared';
import { DialogDescription } from '@radix-ui/react-dialog';

export interface SessionTemplateSelectorDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedTemplateId?: string | null;
    onConfirm: (template: SessionTemplate | null) => void;
    teamId?: string | null;
    confirmDisabled?: boolean;
}

export const SessionTemplateSelectorDialog: React.FC<SessionTemplateSelectorDialogProps> = ({
    open,
    onOpenChange,
    selectedTemplateId = null,
    onConfirm,
    teamId,
    confirmDisabled = false,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [templates, setTemplates] = useState<SessionTemplate[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(selectedTemplateId || null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [columns, setColumns] = useState<number>(3); // default 3 columns

    useEffect(() => {
        setSelectedId(selectedTemplateId || null);
    }, [selectedTemplateId]);

    useEffect(() => {
        if (!open) return;
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const rows = await sessionService.listTemplates(teamId);
                if (!mounted) return;
                setTemplates(rows);
            } catch (err) {
                // ignore; caller handles errors on save
                setTemplates([]);
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [open, teamId]);

    // Reset any UI state when templates change or dialog is opened/closed
    useEffect(() => {
        // reset search when dialog opens/closes or templates change
        setSearchQuery('');
    }, [templates.length, open]);

    const filteredTemplates = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter((t) => {
            const hay = [t.name, t.description || '', t.language || '', t.difficulty || '']
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [templates, searchQuery]);

    // Dynamically compute columns based on window width so dialog reacts to page size
    useEffect(() => {
        function updateColumns() {
            if (typeof window === 'undefined') return;
            const w = window.innerWidth;
            if (w < 640) setColumns(1);
            else if (w < 1024) setColumns(2);
            else setColumns(3);
        }

        updateColumns();
        window.addEventListener('resize', updateColumns);
        return () => window.removeEventListener('resize', updateColumns);
    }, []);

    // pagination removed: render all templates in grid and rely on scroll

    function handleConfirm() {
        const found = templates.find(t => t.id === selectedId) || null;
        onConfirm(found);
        onOpenChange(false);
    }

    function handleClear() {
        setSelectedId(null);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-full max-w-[95vw] sm:max-w-6xl">
                <DialogTitle className="text-lg font-semibold">Templates</DialogTitle>
                <DialogDescription>
                    Choose a template for this session.
                </DialogDescription>

                <CardContent>
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="mb-6 mt-2 w-1/2">
                                <Input
                                    placeholder="Search templates..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    data-testid="template-search"
                                />
                            </div>
                            <div
                                className="overflow-auto rounded-md"
                                style={{ maxHeight: '56vh' }}
                            >
                                {templates.length === 0 ? (
                                    <div className="text-center text-sm text-muted-foreground">No templates found.</div>
                                ) : filteredTemplates.length === 0 ? (
                                    <div className="text-center text-sm text-muted-foreground">No templates match your search.</div>
                                ) : (
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                                            gap: '0.75rem',
                                        }}
                                    >
                                        {filteredTemplates.map((t) => (
                                            <button
                                                key={t.id}
                                                onClick={() => setSelectedId(t.id)}
                                                className={`text-left p-3 rounded-md border transition-colors w-full flex flex-col justify-between items-start cursor-pointer hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 ${selectedId === t.id ? 'border-primary bg-primary/5' : 'border-border'
                                                    }`}
                                                type="button"
                                            >
                                                <div className="w-full">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="font-medium">{t.name}</div>
                                                        <div className="text-sm text-muted-foreground">{t.estimatedDuration}m</div>
                                                    </div>
                                                    <div className="text-sm text-muted-foreground mt-1">{t.language} • {t.difficulty}</div>
                                                    {t.description && (
                                                        <div
                                                            className="text-sm mt-2 text-muted-foreground"
                                                            title={t.description}
                                                        >
                                                            <span
                                                                style={{
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                {t.description}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* pagination removed */}
                        </div>
                    )}
                </CardContent>

                <DialogFooter className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => { handleClear(); onOpenChange(false); }}>Cancel</Button>
                    <div className="flex-1" />
                    <Button onClick={handleConfirm} disabled={loading || confirmDisabled}>Confirm</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SessionTemplateSelectorDialog;
